// =============================================================================
// RunReportScreen (/meldingen) — an external partner reports a faulty run
// =============================================================================
// The Evoluon side of per-run billing. Their operators (role evo_operator)
// see the runs of their own room for one month: start time, PC and title,
// nothing else. No duration, no completion, no restart flag: the reporter
// says what happened and the reviewer holds the evidence. One report per
// run, free text plus an optional photo; the outcome (accepted = not
// invoiced, rejected) and the reviewer's note show up on the same card.
//
// Gated by booking_report_external_runs (route + every RPC); rooms are
// scoped server-side to the operator's location.
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createRunReport,
  listReportRooms,
  listReportRuns,
  uploadReportPhoto,
  type ReporterRoom,
  type ReporterRun,
} from '../lib/runs';
import { formatError } from '../lib/errors';
import { formatVenueDate, formatVenueTime, venueMonthBounds, venueYearMonth } from '../lib/time';
import { RunReportCard } from '../components/RunReportCard';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

// Newest first by default: the run they want to report is usually the one
// that just happened. The choice sticks per device.
type SortOrder = 'newest' | 'oldest';
const SORT_STORAGE_KEY = 'neoseum-admin-meldingen-sort';

function readSortOrder(): SortOrder {
  try {
    return window.localStorage.getItem(SORT_STORAGE_KEY) === 'oldest' ? 'oldest' : 'newest';
  } catch {
    return 'newest';
  }
}

export function RunReportScreen() {
  const [rooms, setRooms] = useState<ReporterRoom[]>([]);
  const [roomId, setRoomId] = useState('');
  const [month, setMonth] = useState(() => venueYearMonth());
  const [sort, setSort] = useState<SortOrder>(() => readSortOrder());
  const [runs, setRuns] = useState<ReporterRun[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listReportRooms()
      .then((rs) => {
        if (cancelled) return;
        setRooms(rs);
        setRoomId((cur) => cur || rs[0]?.room_id || '');
        if (rs.length === 0) setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatError(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bounds = useMemo(() => (month ? venueMonthBounds(month) : null), [month]);

  const load = useCallback(async () => {
    if (!roomId || !bounds) return;
    setLoading(true);
    try {
      setRuns(await listReportRuns(roomId, bounds.startUtc, bounds.endUtc));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [roomId, bounds]);

  useEffect(() => {
    void load();
  }, [load]);

  function changeSort(next: SortOrder) {
    setSort(next);
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }

  // The server returns oldest first; flip the whole list for newest first so
  // days and the runs within a day both follow the chosen order.
  const grouped = useMemo(() => {
    const ordered = [...(runs ?? [])];
    if (sort === 'newest') ordered.reverse();
    const out: { day: string; rows: ReporterRun[] }[] = [];
    for (const r of ordered) {
      const day = formatVenueDate(r.started_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(r);
      else out.push({ day, rows: [r] });
    }
    return out;
  }, [runs, sort]);

  const room = rooms.find((r) => r.room_id === roomId) ?? null;

  return (
    <div className="screen report-screen">
      <h1 className="payments-title">Meldingen</h1>
      <p className="report-intro muted">
        Ging een run fout (bezoeker opnieuw gestart, headset uitgevallen, spel vastgelopen)? Meld het
        bij die run, met een korte omschrijving en eventueel een foto. Neoseum beoordeelt de melding;
        de uitkomst zie je hier terug.
      </p>

      <div className="payments-filters">
        {rooms.length > 1 ? (
          <label className="walkin-field payments-filter">
            <span>Ruimte</span>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {rooms.map((r) => (
                <option key={r.room_id} value={r.room_id}>
                  {r.customer_name} · {r.room_name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="walkin-field payments-filter">
          <span>Maand</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <label className="walkin-field payments-filter">
          <span>Volgorde</span>
          <select value={sort} onChange={(e) => changeSort(e.target.value as SortOrder)}>
            <option value="newest">Nieuwste eerst</option>
            <option value="oldest">Oudste eerst</option>
          </select>
        </label>
      </div>

      {notice ? <div className="muted runs-notice">{notice}</div> : null}
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && rooms.length === 0 && !error ? (
        <div className="muted vouchers-empty">
          Er is geen ruimte aan dit account gekoppeld. Vraag Neoseum om de locatie te zetten.
        </div>
      ) : null}

      {!loading && runs && runs.length === 0 ? (
        <div className="muted vouchers-empty">Geen runs in deze maand.</div>
      ) : null}

      {runs && runs.length > 0 && room
        ? grouped.map((g) => (
            <section key={g.day} className="report-day">
              <h2 className="report-day-title">{g.day}</h2>
              {g.rows.map((run) => (
                <ReporterRunCard
                  key={run.id}
                  run={run}
                  roomId={room.room_id}
                  open={openId === run.id}
                  onOpen={() => {
                    setOpenId(run.id);
                    setNotice(null);
                  }}
                  onClose={() => setOpenId(null)}
                  onFiled={async () => {
                    setOpenId(null);
                    setNotice('Melding verstuurd. Neoseum beoordeelt hem zo snel mogelijk.');
                    await load();
                  }}
                />
              ))}
            </section>
          ))
        : null}
    </div>
  );
}

function ReporterRunCard({
  run,
  roomId,
  open,
  onOpen,
  onClose,
  onFiled,
}: {
  run: ReporterRun;
  roomId: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onFiled: () => Promise<void>;
}) {
  return (
    <div className="report-run">
      <span className="report-run-time">{formatVenueTime(run.started_at)}</span>
      <span className="report-run-pc">{run.pc_name ?? '—'}</span>
      <span className="report-run-exp">{run.experience_name ?? '—'}</span>
      <div className="report-run-body">
        {run.report ? (
          <RunReportCard report={run.report} />
        ) : open ? (
          <ReportForm run={run} roomId={roomId} onCancel={onClose} onFiled={onFiled} />
        ) : (
          <button type="button" className="btn-link" onClick={onOpen}>
            Fout melden
          </button>
        )}
      </div>
    </div>
  );
}

function ReportForm({
  run,
  roomId,
  onCancel,
  onFiled,
}: {
  run: ReporterRun;
  roomId: string;
  onCancel: () => void;
  onFiled: () => Promise<void>;
}) {
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // jsdom (tests) has no object URLs; the preview is a nicety, not the upload.
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = message.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const photoPath = file ? await uploadReportPhoto(roomId, run.id, file) : null;
      await createRunReport(run.id, text, photoPath);
      await onFiled();
    } catch (err) {
      setError(formatError(err));
      setBusy(false);
    }
  }

  return (
    <form className="report-form" onSubmit={(e) => void submit(e)}>
      <label className="walkin-field">
        <span>Wat ging er mis?</span>
        <textarea
          autoFocus
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Bijv. bezoeker zat vast bij de trap, opnieuw gestart"
          disabled={busy}
          required
        />
      </label>
      <label className="report-form-photo">
        <span className="muted">Foto (optioneel)</span>
        <input
          type="file"
          accept={PHOTO_ACCEPT}
          disabled={busy}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {preview ? <img className="report-form-preview" src={preview} alt="Gekozen foto" /> : null}
      </label>
      {error ? <div className="error-card">{error}</div> : null}
      <div className="report-form-actions">
        <button type="submit" className="btn-primary" disabled={busy || message.trim() === ''}>
          {busy ? 'Versturen…' : 'Verstuur melding'}
        </button>
        <button type="button" className="btn-link" onClick={onCancel} disabled={busy}>
          Annuleer
        </button>
      </div>
    </form>
  );
}
